import React, { useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';

import { Flex } from '@ui-library/flex';
import { Badge } from '@ui-library/badge';
import { MenuProps, Dropdown, DropDownProps } from '@ui-library/dropdown';
import { SmallText } from '@ui-library/typography';
import theme from '@css/theme';

type Variant = 'next';
type ColorConfig = {
  color: string;
  iconColor: string;
  bgColor: string;
  hoverColor: string;
};

const variantColors: Record<'default' | Variant, ColorConfig> = {
  default: {
    color: theme.token.colorPrimaryText,
    iconColor: theme.token.colorPrimaryBase,
    bgColor: theme.token['blue.3'],
    hoverColor: theme.token['blue.4'],
  },
  next: {
    color: theme.token.next.palette.grey700,
    iconColor: theme.token.next.palette.grey700,
    bgColor: theme.token.next.palette.blue50,
    hoverColor: theme.token.next.palette.blue50,
  },
};

interface DropdownTagProps {
  label: string;
  icon?: IconProp;
  loading?: boolean;
  variant?: Variant;
  menuItems: MenuProps['items'];
  className?: string;
  placement?: DropDownProps['placement'];
  testId?: string;
}

export const DropdownTag: React.FC<DropdownTagProps> = ({
  label,
  icon,
  variant,
  loading,
  menuItems = [],
  placement = 'bottomRight',
  className,
  testId,
}) => {
  const processedItems = React.useMemo(
    () =>
      menuItems.map((item) => {
        if (!item || !('onClick' in item)) {
          return item;
        }

        const originalOnClick = item.onClick!;
        return {
          ...item,
          'data-testid': item.key,
          onClick: (info: Parameters<NonNullable<MenuProps['onClick']>>[0]) => {
            info.domEvent.stopPropagation();
            info.domEvent.preventDefault();
            originalOnClick(info);
          },
        };
      }),
    [menuItems],
  );

  const isMenuEmpty = menuItems.length === 0;

  const onClickHandler = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  const colors = variantColors[variant ?? 'default'];

  const badge = (
    <Badge
      size="small"
      label={
        <Flex alignItemsCenter gap="0.5rem">
          <SmallText color={colors.color} fontWeight={500} className="text-nowrap">
            {label}
          </SmallText>

          {!isMenuEmpty && <FontAwesomeIcon icon={solid('caret-down')} color={colors.iconColor} />}
        </Flex>
      }
      icon={
        icon ? (
          <FontAwesomeIcon icon={icon} color={colors.iconColor} size={variant ? 'sm' : '1x'} />
        ) : undefined
      }
      bgColor={colors.bgColor}
      hoverColor={isMenuEmpty ? colors.bgColor : colors.hoverColor}
      loading={loading}
      onClick={onClickHandler}
      className={`${className} ${isMenuEmpty ? '' : 'cursor-pointer'}`}
      testId={testId}
      variant={variant}
    />
  );

  return menuItems.length > 0 ? (
    <Dropdown
      variant={variant}
      placement={placement}
      trigger={['click']}
      menu={{ items: processedItems }}
    >
      {badge}
    </Dropdown>
  ) : (
    badge
  );
};
